import React, { useMemo, useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import JsonPathAssistant from './JsonPathAssistant';
import { findSelfParentFkColumn, makeFollowJoinAlias } from './followReference';
import { formatDateOnlyValue, formatDateTimeDisplay } from '../../utils/dateBucketFormat';
import { resolveThresholdBackground, resolveThresholdLabel, isValidColorThresholds } from '../../utils/colorThresholds';
import DateDisplayFormatControl from './DateDisplayFormatControl';
import {
  AVG_PERIOD_SUM_AGG,
  avgPeriodSumHint,
  findAvgPeriodSumRowBucket,
  isAvgPeriodSumAggregation,
} from '../../utils/avgPeriodSum';
import { formatHeatmapPositionCoord } from '../../utils/imageDensityPoints';
import { computeCalculatedValue, formatCalculatedDisplay, normalizeCalculatedColumns } from '../../utils/calculatedColumns';
import CalculatedColumnsEditor from '../CalculatedColumnsEditor';
import { PIVOTABLE_DATE_BUCKETS } from '../../utils/pivotDateBucketTable';
import {
  resolveFollowTarget,
  canFollowFurther,
  getFollowDepth,
  MAX_FOLLOW_DEPTH,
  createRootBrowseScope,
  followBrowseScope,
  buildFieldRefFromBrowse,
  browseScopeFromFieldRef,
  findContextTable,
  preferDisplayColumn,
} from './followReference';

/** Debe coincidir con makeAlias del servidor (joinAlias o sufijo de tabla). */
function fieldAlias(ref, fallback) {
  if (ref?.alias && String(ref.alias).trim()) {
    return String(ref.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60);
  }
  const base = ref?.column || fallback || 'field';
  const jp = ref?.jsonPath ? `_${String(ref.jsonPath).replace(/[^a-zA-Z0-9_]/g, '_')}` : '';
  let alias = `${base}${jp}`;
  if (ref?.jsonMode === 'arrayCount') alias = `${alias}_count`;
  if (ref?.objectKvPart === 'key') {
    return (ref.alias && String(ref.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60)) || 'kv_key';
  }
  if (ref?.objectKvPart === 'value') {
    return (ref.alias && String(ref.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60)) || 'kv_value';
  }
  if (ref?.jsonMode === 'objectKv') alias = `${alias}_object_kv`;
  if (ref?.jsonMode === 'arraySum' && ref.arrayValueField) {
    const f = String(ref.arrayValueField).replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 20);
    alias = `${alias}_sum_${f || 'field'}`;
  }
  if (ref?.jsonMode === 'arrayUnnestGroup') {
    const f = String(ref.arrayElemField || 'field').replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 20);
    alias = `${alias}_grp_${f}`;
  }
  if (ref?.joinAlias) {
    const ja = String(ref.joinAlias)
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .replace(/^(\d)/, '_$1')
      .slice(0, 60);
    if (ja) alias = `${alias}_${ja}`;
  } else {
    const t = String(ref?.tableId || '')
      .split('.')
      .pop()
      .replace(/[^a-zA-Z0-9_]/g, '_')
      .slice(0, 24);
    if (t) alias = `${alias}_${t}`;
  }
  return alias.slice(0, 60) || fallback || 'field';
}

function getColumnMeta(context, ref) {
  if (!ref?.tableId || !ref?.column) return null;
  const table = context?.tables?.find((t) => t.id === ref.tableId);
  const col = table?.columns?.find((c) => c.name === ref.column);
  return col || null;
}

/** Etiqueta técnica (fórmula / origen), ignora alias personalizado. */
function dateBucketLabel(context, bucket) {
  if (!bucket || bucket === 'raw') return '';
  const found = context?.dateBuckets?.find((b) => b.value === bucket);
  return found?.label || bucket;
}

function isNumericScalarJsonPath(catalogPaths, jsonPath) {
  if (!jsonPath || !Array.isArray(catalogPaths)) return false;
  const entry = catalogPaths.find((p) => p.path === jsonPath);
  if (!entry?.selectable) return false;
  const types = entry.types || [];
  return types.includes('number') && !types.includes('array');
}

/** Agregación segura para columnas JSON: no usar SUM hasta tener ruta o modo array. */
function resolveValueAggregation(column, meta) {
  if (
    column?.jsonMode === 'arrayUnnest' ||
    column?.jsonMode === 'objectKv' ||
    column?.jsonMode === 'arrayUnnestGroup'
  ) {
    return 'none';
  }
  if (column?.objectKvPart === 'value') {
    const cur = column?.aggregation;
    return cur && cur !== 'none' ? cur : 'sum';
  }
  if (column?.jsonMode === 'arrayCount' || column?.jsonMode === 'arraySum') {
    const cur = column?.aggregation;
    return cur && cur !== 'none' ? cur : 'sum';
  }
  const jp = String(column?.jsonPath || '').trim();
  if (meta?.logicalType === 'json' && !jp) return 'none';
  if (isNumericScalarJsonPath(meta?.jsonPaths, jp)) {
    const cur = column?.aggregation;
    return cur && cur !== 'none' ? cur : 'sum';
  }
  if (meta?.logicalType === 'json') return 'none';
  const cur = column?.aggregation;
  return cur && cur !== 'none' ? cur : 'sum';
}

function jsonValueNeedsPathConfig(column, meta) {
  if (!meta || meta.logicalType !== 'json') return false;
  if (column?.jsonMode === 'arrayCount' || column?.jsonMode === 'arraySum' || column?.jsonMode === 'arrayUnnest' || column?.jsonMode === 'objectKv') {
    return false;
  }
  return !String(column?.jsonPath || '').trim();
}

/** Escalar o balance (jsonDiff); distinto de modos array/objectKv guardados en columna. */
function isScalarJsonValueMode(jsonMode) {
  return !jsonMode || jsonMode === 'scalar' || jsonMode === 'jsonDiff';
}

function isValueColumn(col) {
  return (
    col?.role === 'value' ||
    col?.jsonMode === 'arrayCount' ||
    col?.jsonMode === 'arraySum' ||
    col?.jsonMode === 'arrayUnnest' ||
    col?.jsonMode === 'objectKv' ||
    col?.objectKvPart === 'value'
  );
}

/** Columna "Fila" real (agrupa/se muestra); excluye Valor y las de Solo filtro (no van en GROUP BY). */
function isRowGroupingColumn(col) {
  return col?.role !== 'filter' && !isValueColumn(col);
}

/** Hay al menos una columna Fila (dimensión de agrupación). */
function hasRowGroupingColumns(columns, excludeIndex = -1) {
  return (columns || []).some((c, i) => {
    if (i === excludeIndex || !c?.tableId || !c?.column) return false;
    return isRowGroupingColumn(c);
  });
}

function rowGroupingUsesDateBucket(columns, context, excludeIndex = -1) {
  for (let i = 0; i < (columns || []).length; i += 1) {
    if (i === excludeIndex) continue;
    const c = columns[i];
    if (!c?.tableId || !c?.column || !isRowGroupingColumn(c)) continue;
    const meta = getColumnMeta(context, c);
    const isDate = meta?.logicalType === 'date' || meta?.logicalType === 'datetime';
    if (isDate && c.dateBucket && c.dateBucket !== 'raw') return true;
  }
  return false;
}

/** Etiquetas específicas: COUNT por fila + agregación al agrupar. */
const ARRAY_COUNT_GROUP_AGGREGATIONS = [
  { value: 'sum', label: 'Suma — total acumulado (todos los días juntos)' },
  { value: 'avgPeriodSum', label: 'Promedio del total por periodo — media del total de cada día' },
  { value: 'avg', label: 'Promedio por registro — media de cada fila (1, 2…)' },
  { value: 'max', label: 'Máximo — pico en un registro' },
  { value: 'min', label: 'Mínimo — mínimo en un registro' },
  { value: 'count', label: 'Conteo — cuántos registros hay' },
];

function buildGroupAggOptions(column, context, allColumns) {
  const hidePeriodSum = (a) => a.value !== 'avgDailySum' && a.value !== 'avgPeriodSum';
  const base =
    column?.jsonMode === 'arrayCount' || column?.jsonMode === 'arraySum'
      ? ARRAY_COUNT_GROUP_AGGREGATIONS
      : (context?.aggregations || []).filter(hidePeriodSum);
  const rowBucket = findAvgPeriodSumRowBucket(allColumns, context, getColumnMeta);
  if (!rowBucket) return base;
  if (column?.jsonMode === 'arrayCount') return base;
  return [...base, AVG_PERIOD_SUM_AGG];
}

function countRowGroupingColumns(columns) {
  return (columns || []).filter((c) => c?.tableId && c?.column && isRowGroupingColumn(c)).length;
}

function technicalLabel(col, context) {
  if (!col?.column) return 'Sin columna';
  if (col.jsonMode === 'arrayCount') {
    const inner = `COUNT(${col.jsonPath || col.column}[])`;
    const agg = col.aggregation && col.aggregation !== 'none' ? col.aggregation : null;
    if (!agg || agg === 'count') return inner;
    if (isAvgPeriodSumAggregation(agg)) return `AVG_PERIOD(SUM(${inner}))`;
    return `${agg.toUpperCase()}(${inner})`;
  }
  if (col.jsonMode === 'arraySum') {
    const field = col.arrayValueField || 'field';
    const inner = `SUM(${col.jsonPath || col.column}[].${field})`;
    const agg = col.aggregation && col.aggregation !== 'none' ? col.aggregation : 'sum';
    if (isAvgPeriodSumAggregation(agg)) return `AVG_PERIOD(${inner})`;
    return `${agg.toUpperCase()}(${inner})`;
  }
  if (col.jsonMode === 'arrayUnnest') {
    const fields = (col.unnestFields || []).map((f) => f.alias || f.path).join(', ');
    return `UNNEST(${col.jsonPath || col.column} → ${fields || '…'})`;
  }
  if (col.jsonMode === 'arrayUnnestGroup') {
    const field = col.arrayElemField || '?';
    const via = col.lookup?.targetTableId
      ? ` → ${col.lookup.targetTableId.split('.').pop()}.${col.lookup.displayColumn || '?'}`
      : '';
    return `GROUP(${col.jsonPath || col.column}[].${field}${via})`;
  }
  if (col.jsonMode === 'objectKv') {
    const agg = col.objectValueAgg && col.objectValueAgg !== 'sum' ? ` · array=${col.objectValueAgg}` : '';
    return `KV(${col.jsonPath || col.column} → kv_key, kv_value${agg})`;
  }
  if (col.joinAlias && col.via?.fromColumn) {
    const d = getFollowDepth(col);
    const dest = (col.tableId || '').split('.').pop() || col.tableId;
    const hop = d > 1 ? ` (×${d})` : '';
    return `${dest}.${col.column} ← ${col.via.fromColumn}${hop}`;
  }
  if (col.role === 'value') {
    const agg = col.aggregation || 'sum';
    if (isAvgPeriodSumAggregation(agg)) {
      return `AVG_PERIOD(SUM(${col.column}${col.jsonPath ? `.${col.jsonPath}` : ''}))`;
    }
    return `${agg.toUpperCase()}(${col.column}${col.jsonPath ? `.${col.jsonPath}` : ''})`;
  }
  let label = col.column;
  if (col.jsonPath) label += `.${col.jsonPath}`;
  if (col.dateBucket && col.dateBucket !== 'raw') {
    label += ` · ${dateBucketLabel(context, col.dateBucket)}`;
  }
  return label;
}

function headerLabel(col, context) {
  if (col?.alias && String(col.alias).trim()) {
    return String(col.alias).trim();
  }
  return technicalLabel(col, context);
}

function previewKeyForColumn(col, index) {
  return fieldAlias(col, col.role === 'value' ? `val_${index}` : `row_${index}`);
}

/**
 * Alias de cada columna "plana" (no Objeto clave/valor ni arrayUnnest) de la
 * lista, agregando el sufijo de dateBucket (o el indice como ultimo recurso) solo
 * cuando dos columnas colisionan en el mismo alias por defecto (mismo campo base con
 * distinto agrupamiento de fecha y sin alias custom). Mismo criterio que el servidor
 * (usedTableAliases en sqlBuilder.js) para que el value usado en los dropdowns de
 * Eje X/Y/Series y en la lectura de filas de la tabla coincida con la clave real que
 * devuelve la consulta SQL. No cambia el alias de columnas sin colision, para no
 * romper graficos ya guardados que dependan del alias plano.
 */
function resolvePlainColumnAliases(columns) {
  const used = new Set();
  const result = new Array((columns || []).length).fill(undefined);
  (columns || []).forEach((col, index) => {
    if (!col) return;
    // Objeto clave/valor y arrayUnnest no compiten por este alias: siempre se
    // expanden en sus propios sub-campos (kv_key/kv_value o por-campo), asi que
    // no entran a la deteccion de colision, pero igual devolvemos el alias plano
    // para no dejar el llamador sin valor.
    if (col.jsonMode === 'objectKv' || col.jsonMode === 'arrayUnnest') {
      result[index] = previewKeyForColumn(col, index);
      return;
    }
    const filterOnly = col.role === 'filter' || col.fieldRole === 'filter';
    let alias = previewKeyForColumn(col, index);
    if (!filterOnly) {
      if (used.has(alias)) {
        const hasCustomAlias = col.alias && String(col.alias).trim();
        const bucketSuffix =
          !hasCustomAlias && col.dateBucket && col.dateBucket !== 'raw'
            ? String(col.dateBucket).replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 20)
            : '';
        // El sufijo por indice refleja la posicion actual en el array; si se
        // agregan o quitan columnas antes, un campo guardado con ese sufijo puede
        // apuntar a otra columna. Mitigar con dateBucket distinto o alias custom.
        alias = bucketSuffix ? `${alias}_${bucketSuffix}`.slice(0, 60) : `${alias}_${index}`.slice(0, 60);
      }
      used.add(alias);
    }
    result[index] = alias;
  });
  return result;
}

const DEFAULT_UNNEST_FIELDS = [
  { path: 'frame.x', alias: 'x' },
  { path: 'frame.y', alias: 'y' },
];

export const DEFAULT_OBJECT_KV_FIELDS = [
  { path: '$key', alias: 'kv_key' },
  { path: '$value', alias: 'kv_value' },
];

/** Debe coincidir con objectKvFieldAlias del servidor (jsonObjectKvExpr.js). */
function objectKvFieldPreviewKey(field, index) {
  if (field?.alias && String(field.alias).trim()) {
    return String(field.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60);
  }
  const base = String(field?.path || `field_${index}`)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .slice(0, 60);
  return base || `kv_${index}`;
}

/** Debe coincidir con unnestFieldAlias del servidor (jsonArrayExpr.js). */
function unnestFieldPreviewKey(field, index) {
  if (field?.alias && String(field.alias).trim()) {
    return String(field.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60);
  }
  const base = String(field?.path || `field_${index}`)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .slice(0, 60);
  return base || `unnest_${index}`;
}

/** Expandir columnas arrayUnnest en una subcolumna por campo proyectado. */
function expandColumnsForPreview(columns, context) {
  const out = [];
  const plainAliases = resolvePlainColumnAliases(columns);
  (columns || []).forEach((col, parentIndex) => {
    if (col?.jsonMode === 'arrayUnnest') {
      const fields = col.unnestFields?.length ? col.unnestFields : DEFAULT_UNNEST_FIELDS;
      fields.forEach((field, fi) => {
        out.push({
          kind: 'unnestField',
          parentIndex,
          parentCol: col,
          field,
          previewKey: unnestFieldPreviewKey(field, fi),
          displayLabel: field.alias || field.path,
        });
      });
      return;
    }
    if (col?.jsonMode === 'objectKv') {
      const fields = col.objectKvFields?.length ? col.objectKvFields : DEFAULT_OBJECT_KV_FIELDS;
      fields.forEach((field, fi) => {
        out.push({
          kind: 'objectKvField',
          parentIndex,
          parentCol: col,
          field,
          previewKey: objectKvFieldPreviewKey(field, fi),
          displayLabel: field.alias || (field.path === '$key' || field.path === 'key' ? 'Clave' : 'Valor'),
        });
      });
      return;
    }
    out.push({
      kind: 'column',
      parentIndex,
      parentCol: col,
      previewKey: plainAliases[parentIndex],
      displayLabel: headerLabel(col, context),
    });
  });
  return out;
}

/** Lado izquierdo de una unión manual: columna ya visible, campo de JSON expandido u objeto clave/valor. */
function adhocSourceFromDisplay(dc) {
  const parent = dc?.parentCol || {};
  const shared = {
    tableId: parent.tableId,
    column: parent.column,
    jsonPath: parent.jsonPath || '',
    joinAlias: parent.joinAlias || '',
    via: parent.via || undefined,
    viaPath: parent.viaPath?.length ? parent.viaPath : undefined,
  };
  if (dc?.kind === 'unnestField') {
    return { kind: 'unnest', path: dc.field?.path, ...shared };
  }
  if (dc?.kind === 'objectKvField') {
    const path = dc.field?.path;
    const part = path === '$key' || path === 'key' ? 'key' : 'value';
    return { kind: 'objectKv', part, ...shared };
  }
  return { kind: 'column', ...shared };
}

function datasetAnchorTableId(columns) {
  const list = columns || [];
  const plain = list.find((c) => c?.tableId && !c.joinAlias && !c.adhocJoin);
  if (plain) return plain.tableId;
  const viaAdhoc = list.find((c) => c?.adhocJoin?.from?.tableId);
  if (viaAdhoc) return viaAdhoc.adhocJoin.from.tableId;
  return null;
}

/** true si el grafo de relaciones confirmadas conecta las dos tablas (en cualquier sentido). */
function hasConfirmedRelationshipPath(context, fromId, toId) {
  const from = findContextTable(context, fromId);
  const to = findContextTable(context, toId);
  const start = from?.id || fromId;
  const goal = to?.id || toId;
  if (!start || !goal) return false;
  if (start === goal) return true;
  const adj = new Map();
  const add = (a, b) => {
    const key = findContextTable(context, a)?.id || a;
    const next = findContextTable(context, b)?.id || b;
    if (!adj.has(key)) adj.set(key, []);
    adj.get(key).push(next);
  };
  for (const rel of context?.relationships || []) {
    if (!rel?.fromTableId || !rel?.toTableId) continue;
    add(rel.fromTableId, rel.toTableId);
    add(rel.toTableId, rel.fromTableId);
  }
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const cur = queue.shift();
    for (const next of adj.get(cur) || []) {
      if (next === goal) return true;
      if (seen.has(next)) continue;
      seen.add(next);
      queue.push(next);
    }
  }
  return false;
}

function makeAdhocJoinAlias(tableId, toColumn, fromKey) {
  const table = String(tableId || '').split('.').pop() || 'ext';
  const raw = `adhoc_${table}_${toColumn}_${fromKey}`;
  return raw.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^(\d)/, '_$1').slice(0, 60) || 'adhoc_join';
}

/** Mismo criterio de identidad de columna que el editor de filtros (tabla + campo). */
function sameColumnRef(f, col) {
  return (
    f.tableId === col.tableId &&
    f.column === col.column &&
    (f.jsonPath || '') === (col.jsonPath || '') &&
    (f.joinAlias || '') === (col.joinAlias || '') &&
    (f.jsonMode || '') === (col.jsonMode || '') &&
    (f.objectKvPart || '') === (col.objectKvPart || '')
  );
}

/** Resumen corto del valor por el que filtra una columna "Solo filtro", para mostrar
 * como referencia en la vista previa (la columna no viene en la fila real). */
function describeFilterValueForColumn(filters, col) {
  const list = filters || [];
  // Coincidencia estricta primero; si no hay match exacto (ej. formato de tableId
  // guardado distinto entre columnas y filtros tras recargar), usar tabla+columna
  // nomas, y como ultimo recurso solo el nombre de columna.
  const f =
    list.find((filter) => sameColumnRef(filter, col)) ||
    list.find((filter) => filter.tableId === col.tableId && filter.column === col.column) ||
    list.find((filter) => filter.column === col.column);
  if (!f) return '';
  const isDynamic = f.layer === 'selection' || f.dynamic;
  const op = f.operator || 'eq';
  if (op === 'between') {
    return isDynamic
      ? `@${f.paramNameStart || 'Desde'} / @${f.paramNameEnd || 'Hasta'}`
      : `${f.rangeStart || '?'} → ${f.rangeEnd || '?'}`;
  }
  if (op === 'isNull') return 'vacío';
  if (op === 'isNotNull') return 'no vacío';
  if (op === 'inTimeRange' || op === 'inLast') return isDynamic ? `@${f.paramName || '?'}` : '';
  if (isDynamic) return `@${f.paramName || '?'}`;
  return String(f.value ?? '');
}

function formatWithDecimalPlaces(value, decimalPlaces) {
  if (decimalPlaces == null || decimalPlaces === '') return null;
  const dp = Number(decimalPlaces);
  if (!Number.isFinite(dp) || dp < 0) return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value).trim());
  if (!Number.isFinite(n)) return null;
  return n.toFixed(Math.min(10, dp));
}

function cellDisplay(value, col) {
  if (value === null || value === undefined) return '';
  const formatted = formatWithDecimalPlaces(value, col?.decimalPlaces);
  if (formatted != null) return formatted;
  const temporal = formatDateTimeDisplay(value, {
    dateDisplayFormat: col?.dateDisplayFormat,
    dateBucket: col?.dateBucket,
  });
  if (temporal != null) return temporal;
  if (typeof value === 'object') {
    if (value instanceof Date) {
      const d = formatDateOnlyValue(value);
      if (d) return d;
    }
    try {
      const s = JSON.stringify(value);
      return s.length > 80 ? `${s.slice(0, 78)}…` : s;
    } catch {
      return String(value);
    }
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (col?.dateBucket === 'hourOfDay' && value >= 0 && value <= 23 && Number.isInteger(value)) {
      return String(value).padStart(2, '0');
    }
    return String(value);
  }
  const s = String(value);
  return s.length > 80 ? `${s.slice(0, 78)}…` : s;
}

/** Lista de columnas del alcance actual (navegación FK). */
function BrowseColumnList({ columns, selected, tableId, context, onSelect }) {
  if (!columns.length) {
    return (
      <Typography variant="caption" color="error">
        Esta tabla no tiene columnas en el catálogo.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        maxHeight: 'min(320px, 40vh)',
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {columns.map((c) => {
        const isSelected = selected === c.name;
        const isFk = Boolean(resolveFollowTarget(context, tableId, c.name));
        return (
          <Box
            key={c.name}
            component="button"
            type="button"
            onClick={() => onSelect(c.name)}
            sx={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              border: 0,
              borderBottom: 1,
              borderColor: 'divider',
              px: 1.25,
              py: 0.75,
              cursor: 'pointer',
              bgcolor: isSelected ? 'action.selected' : 'background.paper',
              '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
              font: 'inherit',
            }}
          >
            <Typography variant="body2" component="span">
              {c.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="span">
              {' '}
              · {c.logicalType}
              {isFk ? ' · FK' : ''}
              {c.supportsJsonPath ? ' · JSON' : ''}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Picker: elige tabla → columnas; en FK, Seguir navega (no añade). */
function AddColumnPopover({
  open,
  anchorEl,
  onClose,
  context,
  onAdd,
  connectionId,
  database,
  getAuthHeaders,
  resultColumns = [],
  datasetColumns = [],
  calculatedColumns = [],
  onChangeCalculatedColumns,
}) {
  const tables = context?.tables || [];
  const [baseTableId, setBaseTableId] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [scopeStack, setScopeStack] = useState([]);
  const [column, setColumn] = useState('');
  const [jsonPath, setJsonPath] = useState('');
  const [jsonMode, setJsonMode] = useState('scalar');
  const [arrayFilters, setArrayFilters] = useState([]);
  const [unnestFields, setUnnestFields] = useState([]);
  const [objectValueAgg, setObjectValueAgg] = useState('sum');
  const [arrayElemField, setArrayElemField] = useState('');
  const [arrayLookup, setArrayLookup] = useState(null);
  const [addKind, setAddKind] = useState('table');
  const [linkFromKey, setLinkFromKey] = useState('');
  const [linkToColumn, setLinkToColumn] = useState('');

  const anchorTableId = datasetAnchorTableId(datasetColumns);
  const needsAdhocJoin = Boolean(
    anchorTableId &&
      baseTableId &&
      anchorTableId !== baseTableId &&
      !hasConfirmedRelationshipPath(context, anchorTableId, baseTableId)
  );

  const scope = scopeStack[scopeStack.length - 1] || null;
  const browseTable = scope ? findContextTable(context, scope.tableId) : null;
  const browseColumns = browseTable?.columns || [];
  const colMeta = browseColumns.find((c) => c.name === column);
  const showJson = Boolean(colMeta?.supportsJsonPath) && (scope?.depth || 0) === 0;
  const selectedFk = column
    ? resolveFollowTarget(context, scope?.tableId, column)
    : null;
  const canFollowSelected =
    Boolean(selectedFk) &&
    (scope?.depth || 0) < MAX_FOLLOW_DEPTH &&
    Boolean(findContextTable(context, selectedFk.targetTableId)?.columns?.length);

  const filteredTables = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    let list = tables;
    if (q) {
      list = tables.filter((t) => {
        const label = String(t.label || t.name || '').toLowerCase();
        const id = String(t.id || '').toLowerCase();
        return label.includes(q) || id.includes(q);
      });
    }
    if (baseTableId && !list.some((t) => t.id === baseTableId)) {
      const selected = tables.find((t) => t.id === baseTableId);
      if (selected) list = [selected, ...list];
    }
    return list;
  }, [tables, tableSearch, baseTableId]);

  useEffect(() => {
    if (open) setTableSearch('');
  }, [open]);

  useEffect(() => {
    if (!baseTableId) {
      setScopeStack([]);
      setColumn('');
      return;
    }
    setScopeStack([createRootBrowseScope(baseTableId)]);
    setColumn('');
    setJsonPath('');
    setJsonMode('scalar');
    setArrayFilters([]);
    setUnnestFields([]);
    setObjectValueAgg('sum');
    setArrayElemField('');
    setArrayLookup(null);
    setLinkFromKey('');
    const table = findContextTable(context, baseTableId);
    const keyCol =
      (table?.columns || []).find((c) => c.isPrimaryKey) ||
      (table?.columns || []).find((c) => /^(id|codigo|code|key)$/i.test(c.name));
    setLinkToColumn(keyCol?.name || '');
  }, [baseTableId, context]);

  const resetAndClose = () => {
    setBaseTableId('');
    setTableSearch('');
    setScopeStack([]);
    setColumn('');
    setJsonPath('');
    setJsonMode('scalar');
    setArrayFilters([]);
    setUnnestFields([]);
    setObjectValueAgg('sum');
    setArrayElemField('');
    setArrayLookup(null);
    setLinkFromKey('');
    setLinkToColumn('');
    onClose();
  };

  const handleFollow = () => {
    if (!scope || !column || !canFollowSelected) return;
    const next = followBrowseScope(context, scope, column);
    if (!next) return;
    const dest = findContextTable(context, next.tableId);
    setScopeStack((stack) => [...stack, next]);
    // Preselecciona un campo legible del destino (el usuario puede cambiarlo)
    setColumn(preferDisplayColumn(dest) || dest?.columns?.[0]?.name || '');
    setJsonPath('');
    setJsonMode('scalar');
    setArrayFilters([]);
    setUnnestFields([]);
    setArrayElemField('');
    setArrayLookup(null);
  };

  const handleBack = () => {
    if (scopeStack.length <= 1) return;
    setScopeStack((stack) => stack.slice(0, -1));
    setColumn('');
  };

  const handleAdd = () => {
    if (!scope || !column) return;
    const field = buildFieldRefFromBrowse(scope, column);
    if (!field) return;
    let adhocJoin;
    let adhocAlias;
    if (needsAdhocJoin) {
      const source = resultColumns.find((col) => col.key === linkFromKey);
      if (!source?.source || !linkToColumn) return;
      adhocAlias = makeAdhocJoinAlias(scope.tableId, linkToColumn, linkFromKey);
      adhocJoin = {
        targetTableId: scope.tableId,
        toColumn: linkToColumn,
        from: source.source,
      };
    }
    const isObjectKv = jsonMode === 'objectKv';
    const isArr = jsonMode === 'arrayCount' || jsonMode === 'arrayUnnest';
    const isGroup = jsonMode === 'arrayUnnestGroup';
    const numericScalar =
      showJson && jsonMode === 'scalar' && isNumericScalarJsonPath(colMeta?.jsonPaths, jsonPath);
    onAdd({
      ...field,
      joinAlias: adhocAlias || field.joinAlias,
      via: adhocJoin ? undefined : field.via,
      viaPath: adhocJoin ? undefined : field.viaPath,
      adhocJoin,
      jsonPath: showJson ? jsonPath : '',
      jsonMode: showJson ? jsonMode : 'scalar',
      arrayFilters: isArr ? arrayFilters : [],
      unnestFields: jsonMode === 'arrayUnnest' ? unnestFields : [],
      arrayElemField: isGroup ? arrayElemField : undefined,
      lookup: isGroup ? arrayLookup || undefined : undefined,
      objectValueAgg: isObjectKv ? objectValueAgg || 'sum' : undefined,
      role: isArr || isObjectKv || numericScalar ? 'value' : 'row',
      aggregation:
        isArr || isObjectKv || numericScalar
          ? resolveValueAggregation(
              { jsonMode, jsonPath, aggregation: 'sum', role: 'value', objectValueAgg },
              colMeta
            )
          : 'none',
    });
    resetAndClose();
  };

  const crumb = [
    baseTableId ? tables.find((t) => t.id === baseTableId)?.label || baseTableId : null,
    ...scopeStack.slice(1).map((s) => s.crumb),
  ]
    .filter(Boolean)
    .join(' → ');

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={resetAndClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      marginThreshold={12}
      PaperProps={{
        sx: {
          width: 'min(480px, calc(100vw - 24px))',
          maxHeight: 'min(80vh, 720px)',
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          width: '100%',
          maxHeight: 'inherit',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle2">Añadir columna</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant={addKind === 'table' ? 'contained' : 'outlined'}
            onClick={() => setAddKind('table')}
          >
            De una tabla
          </Button>
          <Button
            size="small"
            variant={addKind === 'calculated' ? 'contained' : 'outlined'}
            onClick={() => setAddKind('calculated')}
          >
            Calculada
          </Button>
        </Box>

        {addKind === 'calculated' ? (
          <CalculatedColumnsEditor
            columns={resultColumns.map((col) => col.key)}
            labels={Object.fromEntries(resultColumns.map((col) => [col.key, col.label || col.key]))}
            value={calculatedColumns}
            onChange={onChangeCalculatedColumns}
          />
        ) : (
        <>
        <TextField
          size="small"
          fullWidth
          placeholder="Buscar tabla…"
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Tabla</InputLabel>
          <Select
            label="Tabla"
            value={baseTableId}
            onChange={(e) => setBaseTableId(e.target.value)}
            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
          >
            {filteredTables.length === 0 ? (
              <MenuItem disabled value="">
                Sin coincidencias
              </MenuItem>
            ) : (
              filteredTables.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.label || t.name} ({t.id})
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {scope && (
          <>
            {crumb && (
              <Typography variant="caption" color="text.secondary">
                {crumb}
                {(scope.depth || 0) > 0 ? ` · nivel ${scope.depth}/${MAX_FOLLOW_DEPTH}` : ''}
              </Typography>
            )}
            {(scope.depth || 0) > 0 && (
              <Typography variant="caption" color="text.secondary">
                Columnas de la fila referenciada (si la FK es null, el valor queda vacío).
              </Typography>
            )}
            {needsAdhocJoin && (
              <>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Esta tabla no tiene una relación en el modelo. Elige qué columna de los datos ya
                  disponibles coincide con qué columna de esta tabla. Puedes usar un campo que salió
                  de un JSON expandido.
                </Alert>
                <FormControl fullWidth size="small">
                  <InputLabel>Columna de los datos</InputLabel>
                  <Select
                    label="Columna de los datos"
                    value={linkFromKey}
                    onChange={(e) => setLinkFromKey(e.target.value)}
                  >
                    {resultColumns.map((col) => (
                      <MenuItem key={col.key} value={col.key}>
                        {col.label || col.key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Columna de esta tabla</InputLabel>
                  <Select
                    label="Columna de esta tabla"
                    value={linkToColumn}
                    onChange={(e) => setLinkToColumn(e.target.value)}
                  >
                    {browseColumns.map((c) => (
                      <MenuItem key={c.name} value={c.name}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Si la columna de esta tabla se repite, las filas se duplican.
                </Typography>
              </>
            )}
            <BrowseColumnList
              columns={browseColumns}
              selected={column}
              tableId={scope.tableId}
              context={context}
              onSelect={(name) => {
                setColumn(name);
                setJsonPath('');
                setJsonMode('scalar');
                setArrayFilters([]);
                setUnnestFields([]);
                setArrayElemField('');
                setArrayLookup(null);
              }}
            />
          </>
        )}

        {showJson && column && (
          <JsonPathAssistant
            connectionId={connectionId}
            database={database}
            tableId={scope.tableId}
            column={column}
            value={jsonPath}
            jsonMode={jsonMode}
            arrayFilters={arrayFilters}
            unnestFields={unnestFields}
            objectValueAgg={objectValueAgg}
            arrayElemField={arrayElemField}
            lookup={arrayLookup}
            catalogPaths={colMeta?.jsonPaths || null}
            onChange={(patch) => {
              setJsonPath(patch.jsonPath ?? '');
              setJsonMode(patch.jsonMode || 'scalar');
              setArrayFilters(patch.arrayFilters || []);
              setUnnestFields(patch.unnestFields || []);
              setObjectValueAgg(patch.objectValueAgg || 'sum');
              setArrayElemField(patch.arrayElemField ?? '');
              setArrayLookup(patch.lookup !== undefined ? patch.lookup : null);
            }}
            getAuthHeaders={getAuthHeaders}
          />
        )}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(scope?.depth || 0) > 0 && (
            <Button size="small" variant="text" onClick={handleBack}>
              Atrás
            </Button>
          )}
          {canFollowSelected && !needsAdhocJoin && (
            <Button size="small" variant="outlined" onClick={handleFollow}>
              Seguir → {selectedFk.targetTableId.split('.').pop()}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={!scope || !column || (needsAdhocJoin && (!linkFromKey || !linkToColumn))}
            onClick={handleAdd}
          >
            Añadir {column ? `“${column}”` : ''}
          </Button>
        </Box>
        </>
        )}
      </Box>
    </Popover>
  );
}

/**
 * Extrae parte de una columna de texto con una expresión regular (ej. sacar "Sitio" de un
 * nombre de asset). Solo Postgres/Timescale (ver server/columnExpr.js). onChange recibe el
 * transform completo o undefined si se borra el patrón.
 */
function RegexExtractConfig({
  connectionId,
  database,
  getAuthHeaders,
  context,
  tableId,
  column,
  jsonPath,
  joinAlias,
  via,
  viaPath,
  transform,
  coalesceParent,
  onChange,
  onChangeCoalesceParent,
}) {
  const [pattern, setPattern] = useState(transform?.pattern || '');
  const [rawSamples, setRawSamples] = useState([]);
  const [extractedSamples, setExtractedSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const columnSampleMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/column-sample', body, { headers }),
  });

  const selfParentFk = useMemo(() => findSelfParentFkColumn(context, tableId), [context, tableId]);

  const toggleCoalesceParent = (checked) => {
    if (!checked || !selfParentFk) {
      onChangeCoalesceParent?.(undefined);
      return;
    }
    onChangeCoalesceParent?.({
      column,
      joinAlias: makeFollowJoinAlias(selfParentFk.fromColumn),
      via: { fromTableId: tableId, fromColumn: selfParentFk.fromColumn, toColumn: selfParentFk.targetPk },
    });
  };

  useEffect(() => {
    setPattern(transform?.pattern || '');
  }, [tableId, column, jsonPath, joinAlias]);

  const baseSampleBody = {
    connectionId,
    database,
    tableId,
    column,
    jsonPath: jsonPath || undefined,
    joinAlias: joinAlias || undefined,
    via: via || undefined,
    viaPath: viaPath?.length ? viaPath : undefined,
    coalesceParent: coalesceParent || undefined,
    limit: 8,
  };

  useEffect(() => {
    if (!connectionId || !database || !tableId || !column) return undefined;
    const headers = getAuthHeaders?.();
    if (!headers) return undefined;
    let cancelled = false;
    columnSampleMutation
      .mutateAsync({ body: baseSampleBody, headers })
      .then((res) => {
        if (!cancelled && res.data?.success) setRawSamples(res.data.values || []);
      })
      .catch(() => {
        if (!cancelled) setRawSamples([]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, database, tableId, column, jsonPath, joinAlias, coalesceParent]);

  useEffect(() => {
    const trimmed = pattern.trim();
    if (!trimmed || !connectionId || !database || !tableId || !column) {
      setExtractedSamples([]);
      setError(null);
      return undefined;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      columnSampleMutation
        .mutateAsync({
          body: { ...baseSampleBody, transform: { type: 'regexExtract', pattern: trimmed } },
          headers,
        })
        .then((res) => {
          if (cancelled) return;
          if (res.data?.success) {
            setExtractedSamples(res.data.values || []);
          } else {
            setError(res.data?.error || 'Patrón inválido');
            setExtractedSamples([]);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err.response?.data?.error || err.message || 'Error al probar el patrón');
            setExtractedSamples([]);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pattern, connectionId, database, tableId, column, jsonPath, joinAlias, coalesceParent]);

  const commit = (nextPattern) => {
    const trimmed = String(nextPattern ?? pattern).trim();
    onChange(trimmed ? { type: 'regexExtract', pattern: trimmed } : undefined);
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.25 }}>
      <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
        Extraer con expresión regular (opcional)
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Patrón"
        placeholder="ej. .*-\s*([A-Za-zÁÉÍÓÚáéíóúñÑ ]+?)\s*(?:#|[0-9]|\().*$"
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        onBlur={() => commit(pattern)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(pattern);
          }
        }}
        helperText="Usa un grupo de captura (paréntesis): se muestra esa parte del texto. Vacío = usar el valor tal cual. Solo Postgres/Timescale."
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
      />
      {selfParentFk && (
        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={Boolean(coalesceParent)}
              onChange={(e) => toggleCoalesceParent(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Usar el nombre del elemento padre cuando exista
            </Typography>
          }
        />
      )}
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Valores originales de muestra:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: pattern.trim() ? 1 : 0 }}>
          {rawSamples.length ? (
            rawSamples.map((v, i) => (
              <Chip key={i} size="small" variant="outlined" label={String(v)} sx={{ maxWidth: 220 }} />
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              Sin datos de muestra todavía.
            </Typography>
          )}
        </Box>
        {pattern.trim() && (
          <>
            <Typography variant="caption" color="text.secondary" display="block">
              Con este patrón, agrupa en:
            </Typography>
            {loading && <CircularProgress size={16} sx={{ mt: 0.5 }} />}
            {!loading && error && (
              <Alert severity="error" sx={{ py: 0.25, mt: 0.5 }}>
                {error}
              </Alert>
            )}
            {!loading && !error && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {extractedSamples.length ? (
                  extractedSamples.map((v, i) => (
                    <Chip key={i} size="small" color="primary" label={String(v)} sx={{ maxWidth: 220 }} />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Sin resultados con este patrón.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}

function ColumnHeaderMenu({
  open,
  anchorEl,
  onClose,
  column,
  columnIndex,
  allColumns,
  context,
  isSorted,
  orderDirection,
  onPatch,
  onSort,
  onFilter,
  filterObjectKvPart,
  onRemove,
  onMoveColumn,
  onFollowReference,
  connectionId,
  database,
  getAuthHeaders,
}) {
  const meta = getColumnMeta(context, column);
  const showJson = Boolean(meta?.supportsJsonPath);
  const isDate = meta?.logicalType === 'date' || meta?.logicalType === 'datetime';
  const role = column?.role || 'row';
  const hasRowDims = hasRowGroupingColumns(allColumns, columnIndex);
  const rowDimCount = countRowGroupingColumns(allColumns);
  const rowDateBucket = rowGroupingUsesDateBucket(allColumns, context, columnIndex);
  const showGroupAgg =
    (role === 'value' || column?.jsonMode === 'arrayCount' || column?.jsonMode === 'arraySum') &&
    column?.jsonMode !== 'arrayUnnest' &&
    column?.jsonMode !== 'objectKv';
  const aggActive = column?.aggregation && column.aggregation !== 'none';
  const groupAggOptions = buildGroupAggOptions(column, context, allColumns);
  const avgPeriodRowBucket = findAvgPeriodSumRowBucket(allColumns, context, getColumnMeta);
  const aggregationSelectValue = isAvgPeriodSumAggregation(column?.aggregation)
    ? 'avgPeriodSum'
    : column?.aggregation || 'sum';
  const [browseStack, setBrowseStack] = useState([]);
  const [browseColumn, setBrowseColumn] = useState('');
  const [aliasDraft, setAliasDraft] = useState('');
  const canStartFollow = canFollowFurther(context, column);

  const kvPartField = (() => {
    if (column?.jsonMode !== 'objectKv' || !filterObjectKvPart) return null;
    const isKey = filterObjectKvPart === 'key';
    const fields = column.objectKvFields?.length ? column.objectKvFields : DEFAULT_OBJECT_KV_FIELDS;
    const field = fields.find((f) => {
      const path = String(f?.path || '');
      return isKey ? path === '$key' || path === 'key' : path === '$value' || path === 'value';
    });
    const fallback = isKey ? 'kv_key' : 'kv_value';
    const alias = String(field?.alias || '').trim();
    return { isKey, fallback, alias: alias && alias !== fallback ? alias : '' };
  })();

  useEffect(() => {
    if (!open) {
      setBrowseStack([]);
      setBrowseColumn('');
      return;
    }
    if (column?.jsonMode === 'objectKv' && filterObjectKvPart) {
      const isKey = filterObjectKvPart === 'key';
      const fields = column.objectKvFields?.length ? column.objectKvFields : [];
      const field = fields.find((f) => {
        const path = String(f?.path || '');
        return isKey ? path === '$key' || path === 'key' : path === '$value' || path === 'value';
      });
      const fallback = isKey ? 'kv_key' : 'kv_value';
      const alias = String(field?.alias || '').trim();
      setAliasDraft(alias && alias !== fallback ? alias : '');
      return;
    }
    setAliasDraft(column?.alias || '');
  }, [open, column?.tableId, column?.column, column?.joinAlias, column?.alias, column?.jsonMode, column?.objectKvFields, filterObjectKvPart]);

  const browseScope = browseStack[browseStack.length - 1] || null;
  const browseTable = browseScope ? findContextTable(context, browseScope.tableId) : null;
  const browseCols = browseTable?.columns || [];
  const browseFk = browseColumn
    ? resolveFollowTarget(context, browseScope?.tableId, browseColumn)
    : null;
  const canBrowseFollow =
    Boolean(browseFk) &&
    (browseScope?.depth || 0) < MAX_FOLLOW_DEPTH &&
    Boolean(findContextTable(context, browseFk.targetTableId)?.columns?.length);

  const commitAlias = () => {
    const next = String(aliasDraft || '').trim();
    if (column?.jsonMode === 'objectKv' && filterObjectKvPart) {
      const isKey = filterObjectKvPart === 'key';
      const fallback = isKey ? 'kv_key' : 'kv_value';
      const current = column.objectKvFields?.length
        ? column.objectKvFields.map((f) => ({ ...f }))
        : DEFAULT_OBJECT_KV_FIELDS.map((f) => ({ ...f }));
      const idx = current.findIndex((f) => {
        const path = String(f?.path || '');
        return isKey ? path === '$key' || path === 'key' : path === '$value' || path === 'value';
      });
      if (idx < 0) return;
      const prev = String(current[idx].alias || fallback).trim();
      const nextAlias = next || fallback;
      if (nextAlias === prev) return;
      current[idx] = { ...current[idx], alias: nextAlias };
      const bothDefault = current.every((f) => {
        const path = String(f?.path || '');
        if (path === '$key' || path === 'key') return String(f.alias || 'kv_key') === 'kv_key';
        if (path === '$value' || path === 'value') return String(f.alias || 'kv_value') === 'kv_value';
        return false;
      });
      onPatch(columnIndex, { objectKvFields: bothDefault ? undefined : current });
      return;
    }
    const prev = String(column?.alias || '').trim();
    if (next === prev) return;
    onPatch(columnIndex, { alias: next || undefined });
  };

  if (!column) return null;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      marginThreshold={56}
      PaperProps={{
        sx: {
          width: 'min(560px, calc(100vw - 24px))',
          maxHeight: 'min(75vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          p: 2.25,
          width: '100%',
          maxHeight: 'inherit',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography variant="subtitle2" noWrap>
          {kvPartField ? (kvPartField.alias || kvPartField.fallback) : headerLabel(column, context)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap title={technicalLabel(column, context)}>
          {technicalLabel(column, context)}
          {column.tableId ? ` · ${column.tableId}` : ''}
        </Typography>

        <TextField
          size="small"
          fullWidth
          label="Nombre"
          placeholder={kvPartField ? kvPartField.fallback : technicalLabel(column, context)}
          value={aliasDraft}
          onChange={(e) => setAliasDraft(e.target.value)}
          onBlur={commitAlias}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitAlias();
            }
          }}
          helperText="Vacío = nombre automático. También se usa en la tabla/export."
        />

        {column.jsonMode !== 'arrayCount' &&
          column.jsonMode !== 'arraySum' &&
          column.jsonMode !== 'arrayUnnest' &&
          column.jsonMode !== 'arrayUnnestGroup' &&
          column.jsonMode !== 'objectKv' && (
          <FormControl fullWidth size="small">
            <InputLabel>Rol</InputLabel>
            <Select
              label="Rol"
              value={role}
              onChange={(e) => {
                const nextRole = e.target.value;
                onPatch(columnIndex, {
                  role: nextRole,
                  aggregation:
                    nextRole === 'value'
                      ? resolveValueAggregation(column, meta)
                      : 'none',
                });
              }}
            >
              <MenuItem value="row">Fila (agrupar)</MenuItem>
              <MenuItem value="value">Valor (agregar)</MenuItem>
              <MenuItem value="filter">Solo filtro (no se muestra)</MenuItem>
            </Select>
          </FormControl>
        )}

        {role === 'filter' && (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Esta columna no aparece en la tabla ni en el gráfico: solo se usa para filtrar filas
            (por ejemplo, una fecha) sin agruparse ni agregarse.
          </Alert>
        )}

        {role !== 'filter' && (
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={
              <Checkbox
                size="small"
                checked={Boolean(column.displayAsImage)}
                onChange={(e) => onPatch(columnIndex, { displayAsImage: e.target.checked })}
              />
            }
            label={
              <Typography variant="caption">
                Mostrar como imagen (si el valor es una URL, se ve como miniatura en la tabla)
              </Typography>
            }
          />
        )}

        {showJson && role === 'value' && jsonValueNeedsPathConfig(column, meta) && (
          <Alert severity="warning" sx={{ py: 0.25 }}>
            Para contar o sumar sobre JSON: en <strong>Ruta JSON</strong> elige el array (p. ej.{' '}
            <strong>objects</strong>) → <strong>Contar elementos</strong>. La ruta vacía solo sirve
            para ver el JSON crudo sin agregar.
          </Alert>
        )}

        {column.jsonMode === 'arrayCount' && (
          <Typography variant="caption" color="text.secondary">
            Por fila: cuenta elementos del array. Con agrupación temporal en Fila (hora, día de semana…), usa{' '}
            <strong>Promedio del total por periodo</strong> para la media del total de cada día.
          </Typography>
        )}
        {column.jsonMode === 'arraySum' && (
          <Typography variant="caption" color="text.secondary">
            Por fila: suma <strong>{column.arrayValueField || 'campo'}</strong> de cada elemento en{' '}
            <strong>{column.jsonPath || 'array'}[]</strong>. Al agrupar por sensor/tienda, usa Suma o Promedio.
          </Typography>
        )}
        {column.jsonMode === 'arrayUnnest' && (
          <Typography variant="caption" color="text.secondary">
            Modo Expandir: genera una fila por elemento. Debes marcar campos a sacar (p. ej. frame.x /
            frame.y). Para “cuántas persons” usa Contar, no Expandir.
          </Typography>
        )}
        {column.jsonMode === 'objectKv' && (
          <Typography variant="caption" color="text.secondary">
            {String(column.jsonPath || '').includes('*.') ? (
              <>
                Extrae <strong>{String(column.jsonPath).split('.').pop()}</strong> de cada objeto
                (person, car…). <strong>kv_key</strong> es el nombre y <strong>kv_value</strong> el número.
              </>
            ) : (
              <>
                Modo Clave/valor: una fila por cada clave del objeto (incoming, outgoing…). En gráficos de
                líneas elige <strong>kv_value</strong> como medida y <strong>kv_key</strong> como series.
              </>
            )}
          </Typography>
        )}
        {column.jsonMode === 'arrayUnnestGroup' && (
          <Typography variant="caption" color="text.secondary">
            Modo Agrupar: usa <strong>{column.arrayElemField || 'campo'}</strong> de cada elemento como
            Fila.{' '}
            {column.lookup?.targetTableId
              ? <>Muestra <strong>{column.lookup.displayColumn}</strong> buscado en{' '}
                  <strong>{column.lookup.targetTableId}</strong>.</>
              : 'Sin búsqueda: se muestra el valor crudo del campo.'}{' '}
            Agrega una columna Valor con Conteo para contar elementos por grupo.
          </Typography>
        )}

        {showGroupAgg && (
          <>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                <InputLabel>Agregación al agrupar</InputLabel>
                <Select
                  label="Agregación al agrupar"
                  value={aggregationSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    onPatch(columnIndex, {
                      aggregation: v === 'avgPeriodSum' ? 'avgPeriodSum' : v,
                    });
                  }}
                >
                  {groupAggOptions.map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Dec."
                placeholder="Auto"
                value={column.decimalPlaces ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v === '') {
                    onPatch(columnIndex, { decimalPlaces: undefined });
                    return;
                  }
                  const n = Math.min(10, Math.max(0, parseInt(v, 10) || 0));
                  onPatch(columnIndex, { decimalPlaces: n });
                }}
                inputProps={{ min: 0, max: 10, step: 1 }}
                sx={{ width: 72, flexShrink: 0 }}
              />
            </Box>
            {role === 'value' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  type="number"
                  label="Verde hasta"
                  placeholder="Sin semáforo"
                  value={column.colorThresholds?.greenMax ?? ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    if (v === '') {
                      onPatch(columnIndex, { colorThresholds: undefined });
                      return;
                    }
                    onPatch(columnIndex, {
                      colorThresholds: { ...(column.colorThresholds || {}), greenMax: Number(v) },
                    });
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Amarillo hasta"
                  placeholder="Rojo si mayor"
                  disabled={column.colorThresholds?.greenMax === undefined}
                  value={column.colorThresholds?.yellowMax ?? ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    onPatch(columnIndex, {
                      colorThresholds: {
                        ...(column.colorThresholds || {}),
                        yellowMax: v === '' ? undefined : Number(v),
                      },
                    });
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
            )}
            {role === 'value' && isValidColorThresholds(column.colorThresholds) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  label="Etiqueta verde"
                  placeholder="Normal"
                  value={column.colorThresholds?.greenLabel ?? ''}
                  onChange={(e) => {
                    onPatch(columnIndex, {
                      colorThresholds: { ...(column.colorThresholds || {}), greenLabel: e.target.value },
                    });
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  label="Etiqueta amarilla"
                  placeholder="Alerta"
                  value={column.colorThresholds?.yellowLabel ?? ''}
                  onChange={(e) => {
                    onPatch(columnIndex, {
                      colorThresholds: { ...(column.colorThresholds || {}), yellowLabel: e.target.value },
                    });
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
                <TextField
                  size="small"
                  label="Etiqueta roja"
                  placeholder="Crítico"
                  value={column.colorThresholds?.redLabel ?? ''}
                  onChange={(e) => {
                    onPatch(columnIndex, {
                      colorThresholds: { ...(column.colorThresholds || {}), redLabel: e.target.value },
                    });
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                />
              </Box>
            )}
            {column?.jsonMode === 'arrayCount' && column.aggregation === 'avg' && hasRowDims && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                <strong>Promedio por registro</strong> = media de 1, 2… por fila. Para la media del{' '}
                <strong>total diario</strong> (39, 41 → 40), usa <strong>Promedio del total por periodo</strong>.
              </Alert>
            )}
            {isAvgPeriodSumAggregation(column?.aggregation) && avgPeriodRowBucket && (
              <Alert severity="success" sx={{ py: 0.25 }}>
                {avgPeriodSumHint(avgPeriodRowBucket)}
              </Alert>
            )}
            {rowDimCount > 1 && aggActive && (
              <Alert severity="warning" sx={{ py: 0.25 }}>
                Hay {rowDimCount} columnas <strong>Fila</strong>: el grupo se parte en trozos más finos
                (p. ej. id + hora). Deja solo la dimensión de tiempo como Fila si quieres un total por
                hora.
              </Alert>
            )}
            {!hasRowDims && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Sin columna <strong>Fila</strong> no hay colapso: verás el valor por registro (1, 2…).
                Añade la fecha/hora como Fila para agrupar.
              </Alert>
            )}
            {hasRowDims && !rowDateBucket && aggActive && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Para colapsar por hora/día, abre la columna de fecha →{' '}
                <strong>Agrupación temporal</strong> (p. ej. Por hora).
              </Alert>
            )}
          </>
        )}

        {role === 'row' && isDate && (
          <>
            <FormControl fullWidth size="small">
              <InputLabel>Agrupación temporal</InputLabel>
              <Select
                label="Agrupación temporal"
                value={column.dateBucket || 'raw'}
                onChange={(e) => onPatch(columnIndex, { dateBucket: e.target.value })}
              >
                {(context?.dateBuckets || []).map((b) => (
                  <MenuItem key={b.value} value={b.value}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Define cómo colapsar filas (p. ej. Por hora). Las columnas Valor usan «Agregación al
                agrupar» (suma, promedio…) sobre esos grupos.
              </Typography>
            </FormControl>
            <DateDisplayFormatControl
              format={column.dateDisplayFormat}
              dateBucket={column.dateBucket}
              onChange={(dateDisplayFormat) =>
                onPatch(columnIndex, {
                  dateDisplayFormat:
                    !dateDisplayFormat || dateDisplayFormat === 'auto' ? undefined : dateDisplayFormat,
                })
              }
            />
            {PIVOTABLE_DATE_BUCKETS.includes(column.dateBucket) && (
              <FormControlLabel
                sx={{ mt: 0.5 }}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(column.pivotHour)}
                    onChange={(e) => onPatch(columnIndex, { pivotHour: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="caption">Pivote en tabla</Typography>
                }
              />
            )}
          </>
        )}

        {showJson && (
          <JsonPathAssistant
            connectionId={connectionId}
            database={database}
            tableId={column.tableId}
            column={column.column}
            value={column.jsonPath || ''}
            jsonMode={column.jsonMode || 'scalar'}
            arrayFilters={column.arrayFilters || []}
            unnestFields={column.unnestFields || []}
            arrayValueField={column.arrayValueField || ''}
            objectValueAgg={column.objectValueAgg || 'sum'}
            arrayElemField={column.arrayElemField || ''}
            lookup={column.lookup || null}
            catalogPaths={meta?.jsonPaths || null}
            onChange={(patch) => {
              const mode = patch.jsonMode || 'scalar';
              const isArr = mode === 'arrayCount' || mode === 'arraySum' || mode === 'arrayUnnest';
              const isObjectKv = mode === 'objectKv';
              const isGroup = mode === 'arrayUnnestGroup';
              const jsonPath = patch.jsonPath ?? column.jsonPath ?? '';
              const numericScalar = isNumericScalarJsonPath(meta?.jsonPaths, jsonPath);
              const unnestFields =
                mode === 'arrayUnnest'
                  ? patch.unnestFields?.length
                    ? patch.unnestFields
                    : [
                        { path: 'frame.x', alias: 'x' },
                        { path: 'frame.y', alias: 'y' },
                      ]
                  : [];
              onPatch(columnIndex, {
                jsonPath,
                jsonMode: mode,
                arrayFilters: patch.arrayFilters || [],
                unnestFields,
                arrayValueField: mode === 'arraySum' ? patch.arrayValueField || column.arrayValueField || 'probability' : undefined,
                arrayElemField: isGroup ? (patch.arrayElemField ?? column.arrayElemField ?? '') : undefined,
                lookup: isGroup ? (patch.lookup !== undefined ? patch.lookup : column.lookup || null) : undefined,
                objectValueAgg: isObjectKv ? patch.objectValueAgg || column.objectValueAgg || 'sum' : undefined,
                role: isArr || isObjectKv || numericScalar ? 'value' : column.role || 'row',
                aggregation: resolveValueAggregation(
                  {
                    ...column,
                    jsonPath,
                    jsonMode: mode,
                    objectValueAgg: isObjectKv ? patch.objectValueAgg || column.objectValueAgg || 'sum' : undefined,
                    role: isArr || isObjectKv || numericScalar ? 'value' : column.role || 'row',
                  },
                  meta
                ),
              });
            }}
            getAuthHeaders={getAuthHeaders}
          />
        )}

        {meta?.logicalType === 'string' &&
          !['arrayCount', 'arraySum', 'arrayUnnest', 'arrayUnnestGroup', 'objectKv'].includes(column.jsonMode) && (
          <RegexExtractConfig
            connectionId={connectionId}
            database={database}
            getAuthHeaders={getAuthHeaders}
            context={context}
            tableId={column.tableId}
            column={column.column}
            jsonPath={column.jsonPath || ''}
            joinAlias={column.joinAlias || ''}
            via={column.via}
            viaPath={column.viaPath}
            transform={column.transform}
            coalesceParent={column.coalesceParent}
            onChange={(nextTransform) => onPatch(columnIndex, { transform: nextTransform })}
            onChangeCoalesceParent={(next) => onPatch(columnIndex, { coalesceParent: next })}
          />
        )}

        {role === 'value' && column.jsonPath && isScalarJsonValueMode(column.jsonMode) && (
          <TextField
            size="small"
            fullWidth
            label="Balance: restar esta otra ruta (opcional)"
            placeholder="ej. person.outgoing"
            helperText={`Muestra SUM(${column.jsonPath || 'ruta'}) menos SUM(esta ruta), en vez del valor solo`}
            value={column.diffSubtractPath || ''}
            onChange={(e) => {
              const v = e.target.value;
              onPatch(columnIndex, {
                diffSubtractPath: v || undefined,
                jsonMode: v ? 'jsonDiff' : undefined,
              });
            }}
          />
        )}

        <Divider />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            disabled={columnIndex <= 0}
            onClick={() => {
              onMoveColumn(columnIndex, columnIndex - 1);
              onClose();
            }}
          >
            Mover izq.
          </Button>
          <Button
            size="small"
            endIcon={<ArrowForwardIcon />}
            variant="outlined"
            disabled={columnIndex >= allColumns.length - 1}
            onClick={() => {
              onMoveColumn(columnIndex, columnIndex + 1);
              onClose();
            }}
          >
            Mover der.
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<ArrowUpwardIcon />}
            variant={isSorted && orderDirection === 'asc' ? 'contained' : 'outlined'}
            onClick={() => {
              onSort(columnIndex, 'asc');
              onClose();
            }}
          >
            Asc
          </Button>
          <Button
            size="small"
            startIcon={<ArrowDownwardIcon />}
            variant={isSorted && orderDirection === 'desc' ? 'contained' : 'outlined'}
            onClick={() => {
              onSort(columnIndex, 'desc');
              onClose();
            }}
          >
            Desc
          </Button>
        </Box>

        {column?.jsonMode === 'objectKv' && !filterObjectKvPart ? (
          // Este menu representa el par objectKv completo (kv_key + kv_value), sin
          // distincion de cual parte filtrar. Se ofrecen ambas opciones de forma
          // explicita en vez de asumir un valor por defecto.
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => {
                onFilter(columnIndex, null, { objectKvPart: 'key' });
                onClose();
              }}
            >
              Filtrar por clave
            </Button>
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => {
                onFilter(columnIndex, null, { objectKvPart: 'value' });
                onClose();
              }}
            >
              Filtrar por valor
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            startIcon={<FilterListIcon />}
            onClick={() => {
              // No usar e.currentTarget: ese boton vive dentro de este mismo Popover,
              // que se cierra en la misma accion (onClose, abajo) y desaparece del
              // documento. No se pasa ancla: handleFilterColumn (ChartBuilderDialog)
              // cae de vuelta a filterBarRef.current cuando no recibe una, que es la
              // barra de "Filtros activos" (mismo lugar donde se ancla en produccion).
              onFilter(columnIndex, null, {
                objectKvPart: filterObjectKvPart || undefined,
              });
              onClose();
            }}
          >
            Filtrar esta columna
          </Button>
        )}

        {canStartFollow && !browseScope && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const next = browseScopeFromFieldRef(context, column);
              if (!next) return;
              const dest = findContextTable(context, next.tableId);
              setBrowseStack([next]);
              setBrowseColumn(
                preferDisplayColumn(dest) || dest?.columns?.[0]?.name || ''
              );
            }}
          >
            Seguir referencia…
          </Button>
        )}

        {browseScope && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Columnas en {browseScope.tableId}
              {browseScope.depth ? ` · nivel ${browseScope.depth}/${MAX_FOLLOW_DEPTH}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Elige el campo y pulsa Añadir. Seguir solo entra en otra FK.
            </Typography>
            <BrowseColumnList
              columns={browseCols}
              selected={browseColumn}
              tableId={browseScope.tableId}
              context={context}
              onSelect={setBrowseColumn}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  if (browseStack.length <= 1) {
                    setBrowseStack([]);
                    setBrowseColumn('');
                    return;
                  }
                  setBrowseStack((s) => s.slice(0, -1));
                  setBrowseColumn('');
                }}
              >
                Atrás
              </Button>
              {canBrowseFollow && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const next = followBrowseScope(context, browseScope, browseColumn);
                    if (!next) return;
                    const dest = findContextTable(context, next.tableId);
                    setBrowseStack((s) => [...s, next]);
                    setBrowseColumn(
                      preferDisplayColumn(dest) || dest?.columns?.[0]?.name || ''
                    );
                  }}
                >
                  Seguir → {browseFk.targetTableId.split('.').pop()}
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                disabled={!browseColumn}
                onClick={() => {
                  const field = buildFieldRefFromBrowse(browseScope, browseColumn);
                  if (!field) return;
                  onFollowReference?.(field);
                  onClose();
                }}
              >
                Añadir “{browseColumn || '…'}”
              </Button>
            </Box>
          </Box>
        )}

        <Button size="small" color="error" onClick={() => { onRemove(columnIndex); onClose(); }}>
          Quitar columna
        </Button>
      </Box>
    </Popover>
  );
}

/**
 * Tabla viva: encabezados configurables + columna "+" para añadir campos.
 */
export default function LiveDatasetTable({
  columns = [],
  previewData = [],
  previewLoading = false,
  context,
  connectionId,
  database,
  getAuthHeaders,
  orderColumnIndex = 0,
  orderDirection = 'desc',
  onChangeColumns,
  onSort,
  onFilterColumn,
  emptyHint = 'Añade una columna con +',
  filters = [],
  overlayColumns = [],
  calculatedColumns = [],
  onChangeCalculatedColumns,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuIndex, setMenuIndex] = useState(null);
  const [menuObjectKvPart, setMenuObjectKvPart] = useState(null);
  const [addAnchor, setAddAnchor] = useState(null);

  const displayColumns = useMemo(
    () => expandColumnsForPreview(columns, context),
    [columns, context]
  );
  const resultColumns = useMemo(() => {
    const opts = [];
    const seen = new Set();
    displayColumns.forEach((dc) => {
      if (dc.kind === 'column' && dc.parentCol?.role === 'filter') return;
      const key = dc.previewKey;
      if (!key || seen.has(key)) return;
      seen.add(key);
      opts.push({ key, label: dc.displayLabel || key, source: adhocSourceFromDisplay(dc) });
    });
    return opts;
  }, [displayColumns]);

  const patchColumn = (index, patch) => {
    onChangeColumns?.(columns.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const removeColumn = (index) => {
    onChangeColumns?.(columns.filter((_, i) => i !== index));
  };

  const moveColumn = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= columns.length || fromIndex === toIndex) return;
    const next = [...columns];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    onChangeColumns?.(next, { movedFrom: fromIndex, movedTo: toIndex });
  };

  const addColumn = (field) => {
    onChangeColumns?.([
      ...columns,
      {
        id: `col-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        ...field,
      },
    ]);
  };

  const handleFollowReference = (fieldRef) => {
    if (!fieldRef?.tableId || !fieldRef?.column) return;
    const exists = columns.some(
      (c) =>
        c.joinAlias === fieldRef.joinAlias &&
        c.column === fieldRef.column &&
        c.tableId === fieldRef.tableId
    );
    if (exists) return;
    addColumn(fieldRef);
  };

  const rowsToShow = previewData.slice(0, 100);
  const hasConfigured = columns.some((c) => c.tableId && c.column);
  const overlayCols = Array.isArray(overlayColumns) ? overlayColumns : [];
  const calcDefs = useMemo(
    () => normalizeCalculatedColumns(
      calculatedColumns,
      previewData?.[0] ? Object.keys(previewData[0]) : []
    ),
    [calculatedColumns, previewData]
  );
  const tableColCount = Math.max(displayColumns.length, 0) + overlayCols.length + calcDefs.length + 1;

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {previewLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1,
            bgcolor: rowsToShow.length === 0 ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.45)',
            pointerEvents: rowsToShow.length === 0 ? 'auto' : 'none',
          }}
        >
          {rowsToShow.length === 0 ? (
            <>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Cargando datos…
              </Typography>
            </>
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Actualizando…
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {displayColumns.map((dc, idx) => {
                const col = dc.parentCol;
                const sorted = orderColumnIndex === dc.parentIndex;
                const isFilterOnly = dc.kind === 'column' && col.role === 'filter';
                const isValue =
                  dc.kind === 'unnestField' ||
                  dc.kind === 'objectKvField' ||
                  (dc.field?.path === '$value' || dc.field?.path === 'value') ||
                  col.role === 'value';
                const headerTitle =
                  dc.kind === 'unnestField'
                    ? `${dc.field.path}${dc.field.alias ? ` → ${dc.field.alias}` : ''}`
                    : dc.kind === 'objectKvField'
                      ? `${dc.displayLabel}${dc.field.alias ? ` (${dc.field.alias})` : ''}`
                      : headerLabel(col, context);
                return (
                  <TableCell
                    key={
                      dc.kind === 'unnestField'
                        ? `${col.id || dc.parentIndex}-unnest-${dc.previewKey}`
                        : dc.kind === 'objectKvField'
                          ? `${col.id || dc.parentIndex}-kv-${dc.previewKey}`
                          : col.id || `${col.tableId}.${col.column}.${dc.parentIndex}`
                    }
                    sx={{
                      bgcolor: isFilterOnly ? 'warning.50' : isValue ? 'primary.50' : 'grey.100',
                      borderBottom: 2,
                      borderColor: 'divider',
                      minWidth: 140,
                      py: 0.75,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Box
                        sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuIndex(dc.parentIndex);
                          if (dc.kind === 'objectKvField') {
                            const path = String(dc.field?.path || '').trim();
                            setMenuObjectKvPart(
                              path === '$key' || path === 'key' ? 'key' : 'value'
                            );
                          } else {
                            setMenuObjectKvPart(null);
                          }
                        }}
                      >
                        <Chip
                          size="small"
                          label={isFilterOnly ? 'Filtro' : isValue ? 'Valor' : 'Fila'}
                          color={isFilterOnly ? 'warning' : isValue ? 'primary' : 'default'}
                          sx={{ height: 18, fontSize: 10, mb: 0.25 }}
                        />
                        <Typography variant="body2" fontWeight={600} noWrap title={headerTitle}>
                          {dc.displayLabel}
                          {sorted && (
                            <Box component="span" sx={{ ml: 0.5, opacity: 0.7 }}>
                              {orderDirection === 'asc' ? '↑' : '↓'}
                            </Box>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {(col.tableId || '').split('.').pop()}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuIndex(dc.parentIndex);
                          if (dc.kind === 'objectKvField') {
                            const path = String(dc.field?.path || '').trim();
                            setMenuObjectKvPart(
                              path === '$key' || path === 'key' ? 'key' : 'value'
                            );
                          } else {
                            setMenuObjectKvPart(null);
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                );
              })}
              {calcDefs.map((def) => (
                <TableCell
                  key={`calc-${def.id}`}
                  sx={{
                    bgcolor: 'grey.100',
                    borderBottom: 2,
                    borderColor: 'divider',
                    minWidth: 140,
                    py: 0.75,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Chip size="small" label="Calculada" color="secondary" sx={{ height: 18, fontSize: 10, mb: 0.25 }} />
                      <Typography variant="body2" fontWeight={600} noWrap title={def.label}>
                        {def.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block" title={def.key}>
                        {def.key}
                      </Typography>
                    </Box>
                    {onChangeCalculatedColumns && (
                      <IconButton
                        size="small"
                        title="Quitar columna calculada"
                        onClick={() => onChangeCalculatedColumns(
                          calculatedColumns.filter((item) => item.id !== def.id && item.key !== def.key)
                        )}
                      >
                        <Typography variant="caption" color="error">✕</Typography>
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
              {overlayCols.map((oc) => (
                <TableCell
                  key={`overlay-${oc.key}`}
                  sx={{
                    bgcolor: oc.temporary ? 'warning.50' : 'success.50',
                    borderBottom: 2,
                    borderColor: 'divider',
                    minWidth: 140,
                    py: 0.75,
                  }}
                >
                  <Chip
                    size="small"
                    label={oc.temporary ? 'Temporal' : 'Posición'}
                    color={oc.temporary ? 'warning' : 'success'}
                    sx={{ height: 18, fontSize: 10, mb: 0.25 }}
                  />
                  <Typography variant="body2" fontWeight={600} noWrap title={oc.label}>
                    {oc.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    Heatmap
                  </Typography>
                </TableCell>
              ))}
              <TableCell
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: 2,
                  borderColor: 'divider',
                  width: 56,
                  px: 0.5,
                }}
              >
                <IconButton
                  color="primary"
                  size="small"
                  title="Añadir columna"
                  onClick={(e) => setAddAnchor(e.currentTarget)}
                >
                  <AddIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasConfigured ? (
              <TableRow>
                <TableCell colSpan={tableColCount} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">{emptyHint}</Typography>
                  <Button
                    sx={{ mt: 1 }}
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={(e) => setAddAnchor(e.currentTarget)}
                  >
                    Añadir columna
                  </Button>
                </TableCell>
              </TableRow>
            ) : previewLoading && rowsToShow.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColCount} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">Preparando vista previa…</Typography>
                </TableCell>
              </TableRow>
            ) : rowsToShow.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColCount} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Sin filas en la preview</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rowsToShow.map((row, ri) => (
                <TableRow key={ri} hover>
                  {displayColumns.map((dc, ci) => {
                    const col = dc.parentCol;
                    const isFilterOnlyForRow = dc.kind === 'column' && col.role === 'filter';
                    const key = dc.previewKey;
                    let raw;
                    // Una columna "Solo filtro" no participa del SELECT, asi que su
                    // previewKey puede coincidir por accidente con el de otra columna
                    // real del mismo campo (ej. "time" agrupado y "time" del filtro).
                    // No leer la fila para estas columnas evita mostrar un dato ajeno.
                    if (!isFilterOnlyForRow) {
                      raw = row[key];
                      if (raw === undefined && key) {
                        const hit = Object.keys(row || {}).find(
                          (k) => k.toLowerCase() === String(key).toLowerCase()
                        );
                        if (hit) raw = row[hit];
                      }
                    }
                    const filterRefLabel = isFilterOnlyForRow
                      ? describeFilterValueForColumn(filters, col)
                      : undefined;
                    const displayText = isFilterOnlyForRow ? filterRefLabel || '' : cellDisplay(raw, col);
                    const cellBg = isFilterOnlyForRow ? null : resolveThresholdBackground(raw, col?.colorThresholds);
                    const cellLabel = isFilterOnlyForRow ? null : resolveThresholdLabel(raw, col?.colorThresholds);
                    return (
                      <TableCell
                        key={
                          dc.kind === 'unnestField'
                            ? `${col.id || dc.parentIndex}-unnest-${dc.previewKey}-${ci}`
                            : dc.kind === 'objectKvField'
                              ? `${col.id || dc.parentIndex}-kv-${dc.previewKey}-${ci}`
                              : col.id || ci
                        }
                        sx={{ maxWidth: 220, ...(cellBg ? { backgroundColor: cellBg } : {}) }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          title={cellLabel ? `${displayText} (${cellLabel})` : displayText}
                          color={isFilterOnlyForRow ? 'text.secondary' : 'text.primary'}
                          fontStyle={isFilterOnlyForRow ? 'italic' : 'normal'}
                        >
                          {displayText}
                          {cellLabel ? ` (${cellLabel})` : ''}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  {calcDefs.map((def) => {
                    const value = computeCalculatedValue(row, def);
                    const text = formatCalculatedDisplay(value, def);
                    return (
                      <TableCell key={`calc-${def.id}-${ri}`} sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" noWrap title={text}>
                          {text}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  {overlayCols.map((oc) => (
                    <TableCell key={`overlay-${oc.key}-${ri}`} sx={{ maxWidth: 220 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={formatHeatmapPositionCoord(row[oc.key]) || '—'}
                      >
                        {formatHeatmapPositionCoord(row[oc.key]) || '—'}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasConfigured && previewData.length > 100 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.5 }}>
          Mostrando 100 de {previewData.length} filas
        </Typography>
      )}

      <ColumnHeaderMenu
        // El refresco de la vista previa (actualizar datos, validacion de sesion, etc.)
        // vuelve a renderizar los encabezados de columna; si el menu seguia abierto,
        // su anchorEl queda apuntando a un nodo que ya no esta en el documento. Se
        // verifica que siga adjunto antes de pedirle a MUI que lo use como ancla,
        // para no disparar el warning "anchorEl prop provided to the component is invalid".
        open={Boolean(menuAnchor) && menuIndex != null && document.body.contains(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={() => {
          setMenuAnchor(null);
          setMenuIndex(null);
          setMenuObjectKvPart(null);
        }}
        column={menuIndex != null ? columns[menuIndex] : null}
        columnIndex={menuIndex}
        filterObjectKvPart={menuObjectKvPart}
        allColumns={columns}
        context={context}
        isSorted={menuIndex === orderColumnIndex}
        orderDirection={orderDirection}
        onPatch={patchColumn}
        onSort={onSort}
        onFilter={onFilterColumn}
        onRemove={removeColumn}
        onMoveColumn={moveColumn}
        onFollowReference={handleFollowReference}
        connectionId={connectionId}
        database={database}
        getAuthHeaders={getAuthHeaders}
      />

      <AddColumnPopover
        open={Boolean(addAnchor) && document.body.contains(addAnchor)}
        anchorEl={addAnchor}
        onClose={() => setAddAnchor(null)}
        context={context}
        onAdd={addColumn}
        connectionId={connectionId}
        database={database}
        getAuthHeaders={getAuthHeaders}
        resultColumns={resultColumns}
        datasetColumns={columns}
        calculatedColumns={calculatedColumns}
        onChangeCalculatedColumns={onChangeCalculatedColumns}
      />
    </Paper>
  );
}

export { fieldAlias, getColumnMeta, headerLabel, technicalLabel, resolvePlainColumnAliases };
